"use client";

import Link from "next/link";
import type { MarketingLocale } from "@/lib/seo/site";
import { PublicHeaderControls } from "@/components/PublicHeaderControls";
import { useLanguage } from "@/lib/useLanguage";
import { MenuHunterLockup } from "@/components/brand/MenuHunterLockup";

type NavItem = { href: string; label: string };

const NAV_BY_LOCALE: Record<MarketingLocale, NavItem[]> = {
  es: [
    { href: "/community", label: "Comunidad" },
    { href: "/explore", label: "Mapa" },
    { href: "/es/menu-del-dia-barcelona", label: "Barcelona" },
    { href: "/restaurants", label: "Para restaurantes" },
  ],
  en: [
    { href: "/community", label: "Community" },
    { href: "/explore", label: "Map" },
    { href: "/en/menu-del-dia-barcelona", label: "Barcelona" },
    { href: "/restaurants", label: "For restaurants" },
  ],
  ca: [
    { href: "/community", label: "Comunitat" },
    { href: "/explore", label: "Mapa" },
    { href: "/ca/menu-del-dia-barcelona", label: "Barcelona" },
    { href: "/restaurants", label: "Per a restaurants" },
  ],
};
const DOWNLOAD_LABEL: Record<MarketingLocale, string> = {
  es: "Descargar",
  en: "Download",
  ca: "Descarregar",
};

const HOME_ARIA: Record<MarketingLocale, string> = {
  es: "MenúHunter inicio",
  en: "MenúHunter home",
  ca: "MenúHunter inici",
};

type MarketingHeaderProps = {
  /** When set, nav labels and discover link match the SEO locale. Defaults to Spanish (homepage). */
  locale?: MarketingLocale;
};

export function MarketingHeader({ locale: localeProp }: MarketingHeaderProps) {
  const { language } = useLanguage();
  const locale = localeProp ?? language;
  const navLinks = NAV_BY_LOCALE[locale];

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 dark:border-slate-800 dark:bg-slate-950/90 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-4">
        <Link
          href="/"
          className="flex items-center gap-2 shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-lg"
          aria-label={HOME_ARIA[locale]}
        >
          <MenuHunterLockup
            markSize={42}
            strokeWidth={15.5}
            variant="micro"
            wordmarkClassName="font-semibold text-[#0F172A] dark:text-white text-sm sm:text-base hidden min-[400px]:inline"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 text-sm" aria-label="Principal">
          {navLinks.map((item) => (
            <Link
              key={item.href + item.label}
              href={item.href}
              className="px-2.5 py-2 rounded-lg text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors whitespace-nowrap"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <PublicHeaderControls />
          <Link
            href="/download"
            className="shrink-0 px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950"
          >
            {DOWNLOAD_LABEL[locale]}
          </Link>
        </div>
      </div>
    </header>
  );
}
